import Vue from 'vue';

import L from 'leaflet';
import 'leaflet-easybutton';
import 'leaflet/dist/leaflet.css';
import 'leaflet-easybutton/src/easy-button.css';

import estilos from '@/utils/mapa/mapa.estilos';

import ControleLeafletComComponenteVue from '@/utils/mapa/ControleLeafletComComponenteVue';
import CamadasBase from '@/components/mapa/camadasBase/CamadasBase.vue';
import Camadas from '@/components/mapa/camadas/Camadas.vue';

import { DEFAULT_CONFIG, BASE_LAYERS_CONFIG } from '@/utils/mapa/mapa.config';

export default class Mapa {
    constructor(idElemento, config = {}, onClick = null) {
        this.setConfig(config);

        this.map = L.map(idElemento, this.config);
        this.adicionarCamadaBase();
        this.configurarControles();

        this.setBoundsDefault(this.map.getBounds());

        if (onClick) {
            this.map.on('click', onClick);
        }
    }

    get mapa() {
        return this.map;
    }

    get centro() {
        return this.map.getCenter();
    }

    setBoundsDefault(limite) {
        this.bounds = limite;
    }

    refresh() {
        this.map.invalidateSize();
    }

    adicionarBotao(options, position = 'topright', id = null) {
        const states = options instanceof Array ? options : [options];
        const config = {
            position,
            states,
            id
        };

        const button = L.easyButton(config);
        button.addTo(this.map);

        return button;
    }

    static criarMarcador(latlng, icone) {
        const icon = icone || estilos.Marcador.padrao;
        const marcador = L.marker(latlng, { icon });

        return marcador;
    }

    criarEAdicionarMarcador(latlng, icone) {
        const marcador = Mapa.criarMarcador(latlng, icone);
        this.map.addLayer(marcador);
        return marcador;
    }

    adicionarPoligono(latlngs, estilo) {
        const style = estilo || estilos.Geometria.default;
        const poligono = L.polygon(latlngs, { style });
        this.map.addLayer(poligono);

        return poligono;
    }

    adicionarGeoJSON(geojson, estilo) {
        const geometria = Mapa.criarGeoJSON(geojson, estilo);

        this.map.addLayer(geometria);

        return geometria;
    }

    static criarGeoJSON(geojson, estilo) {
        const json = geojson instanceof String ? JSON.parse(geojson) : geojson;
        const style = estilo || estilos.Geometria.default;
        return L.geoJSON(json, { style });
    }

    removerDoMapa(camada) {
        this.map.removeLayer(camada);
    }

    static centralizarEm(geometria) {
        if (geometria.getBounds) {
            this.map.fitBounds(geometria.getBounds());
        } else if (geometria.getLatLng) {
            this.map.setView(geometria.getLatLng(), 13);
        } else if (
            geometria instanceof L.LatLng ||
            geometria instanceof Array
        ) {
            this.map.setView(geometria, 13);
        } else if (geometria instanceof L.LatLngBounds) {
            this.map.fitBounds(geometria);
        }
    }

    static adicionarPopup(geometria, conteudo, options = {}) {
        if (!geometria.bindPopup) return null;

        let popup;

        if (typeof conteudo === 'string') {
            popup = L.popup(options);
            popup.setContent(conteudo);
        } else if (conteudo.render && conteudo._scopeId) {
            const popupOptions = {
                className: 'custom-popup',
                closeButton: false,
                minWidth: 300,
                maxWidth: 500
            };
            popup = L.popup(Object.assign(popupOptions, options));
            geometria.on('popupopen', event => {
                new Vue({
                    render: h => h(conteudo, { props: options.props })
                }).$mount(event.popup._wrapper.children[0]);
            });
        }

        geometria.bindPopup(popup);

        return popup;
    }

    adicionarIconePersonalizado(latlng, content, style) {
        const myIcon = L.divIcon({ className: style, html: content });
        const marker = L.marker(latlng, { icon: myIcon }).addTo(this.map);
        return marker;
    }

    setConfig(config) {
        const controles = Object.assign(
            DEFAULT_CONFIG.controles,
            config.controles
        );
        this.config = Object.assign(DEFAULT_CONFIG, config);
        this.config.controles = controles;
    }

    adicionarCamadaBase() {
        const configCamadas = BASE_LAYERS_CONFIG;

        const config = {
            classeControle: 'leaflet-control-camadas-base',
            position: 'bottomright'
        };
        ControleLeafletComComponenteVue(
            CamadasBase,
            this.mapa,
            { configCamadas },
            config
        );
    }

    adicionarControleCamadas() {
        const config = {
            classeControle: 'leaflet-control-camadas-base',
            position: 'bottomleft'
        };
        ControleLeafletComComponenteVue(Camadas, this.mapa, {}, config);
    }

    configurarControles() {
        if (this.config.controles.zoom.enabled) {
            this.map.addControl(
                L.control.zoom({
                    zoomInTitle: 'Aproximar',
                    zoomOutTitle: 'Afastar',
                    position: this.config.controles.zoom.position
                })
            );
        }

        if (this.config.controles.fullscreen.enabled) {
            this.map.addControl(
                new L.Control.Fullscreen({
                    title: {
                        false: 'Exibir mapa em tela cheia',
                        true: 'Sair do modo de tela cheia'
                    },
                    position: this.config.controles.fullscreen.position
                })
            );
        }

        if (this.config.controles.escala.enabled) {
            L.control.scale().addTo(this.map);
        }

        if (this.config.controles.centralizar.enabled) {
            this.adicionarControleCentralizar();
        }

        this.adicionarControleCamadas();
    }

    adicionarControleCentralizar() {
        this.adicionarBotao(
            {
                icon: 'mdi mdi-target',
                title: 'Voltar à posição inicial do mapa',
                onClick: () => {
                    this.posicaoInicial();
                }
            },
            this.config.controles.centralizar.position
        );
    }

    posicaoInicial() {
        try {
            if (this.bounds) {
                return this.map.fitBounds(this.bounds);
            }
        } catch (error) {
            Notification.warning({
                title: 'Posição inicial',
                message: 'Não foi possível retornar a posição inicial do mapa.',
                duration: 8000
            });
        }
        return null;
    }
}
